/**
 * CalendarModal: 「別の日時から選ぶ」「都合の良い日時がない場合はこちら」「アポイントをリクエストする」
 * 展開時に共通で使う中央カレンダーモーダル。design-mockups/*.html（2026-08-06最終版）準拠。
 *
 * モック開発過程では画面ごとに異なる展開方式（カード内インライン展開→ポップオーバー等）を
 * 試したが、レイアウトの複雑化・位置ズレの指摘を重ねた結果、全画面共通で単一の中央モーダルに
 * 統一された（`openCalendarModal`汎用関数、既存要素をモーダルへ一時移動し閉じたら元に戻す方式）。
 * カタログDL完了画面では、外側の複数CVモーダル（画面中央、背景暗転）よりさらに手前に重ねて表示する。
 */

import { createElement } from "../core/shadowDomUtil";
import { TEXT_PRIMARY, TEXT_SECONDARY, SHADOW_MODAL, RADIUS_DEFAULT, FONT_FAMILY } from "./designTokens";

export const CALENDAR_MODAL_STYLES = `
:host { all: initial; }
* { box-sizing: border-box; }
.spica-calendar-modal-backdrop {
  position: fixed; inset: 0; z-index: 2147483647;
  background: rgba(51, 51, 51, .6);
  display: flex; align-items: center; justify-content: center;
  padding: 40px 24px;
}
.spica-calendar-modal-backdrop[hidden] { display: none; }
.spica-calendar-modal-panel {
  position: relative; box-sizing: border-box;
  width: min(480px, 100%); max-height: calc(100vh - 80px); overflow-y: auto;
  background: #fff; border-radius: ${RADIUS_DEFAULT}; box-shadow: ${SHADOW_MODAL};
  padding: 28px 28px 24px; font-family: ${FONT_FAMILY}; color: ${TEXT_PRIMARY};
}
.spica-calendar-modal-close {
  position: absolute; top: 20px; right: 22px; border: none; background: transparent;
  font-size: 20px; line-height: 1; color: ${TEXT_SECONDARY}; cursor: pointer;
}
.spica-calendar-modal-close:hover { color: ${TEXT_PRIMARY}; }
.spica-calendar-modal-company {
  font-size: 16px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; padding-right: 28px;
}
.spica-calendar-modal-content {
  font-size: 13px; color: ${TEXT_SECONDARY}; margin: 0 0 16px;
}
.spica-calendar-modal-body { margin-top: 4px; }
`;

export interface CalendarModalOpenOptions {
  /** モーダルのヘッダーに表示する企業名（省略時は非表示）。 */
  company?: string;
  /** モーダルのヘッダーに表示するコンテンツ名（省略時は非表示）。 */
  content?: string;
  /** モック準拠のDOM並び替え（カレンダー→候補欄横並び）を適用するか。デフォルトfalse。 */
  reorderBody?: boolean;
}

export interface CalendarModalHandle {
  /** モーダルのルート要素（Shadow Root配下へ追加する）。 */
  element: HTMLElement;
  /**
   * 指定の要素（候補欄・カレンダー・時間帯選択・送信ボタン等を含む構築済みDOM）を
   * モーダル内へ一時的に移動して表示する。`onClose`は×ボタン・背景クリック・Escape・
   * `close()`呼び出しのいずれで閉じても呼ばれる（呼び出し元の状態リセット用）。
   * `options`で企業名・コンテンツ名を渡すとモーダルヘッダーに表示する。
   */
  openWithElement: (bodyElement: HTMLElement, onClose?: () => void, options?: CalendarModalOpenOptions) => void;
  /** モーダルを閉じ、直前に`openWithElement`で渡された要素を元の位置に戻す。 */
  close: () => void;
}

/**
 * 中央カレンダーモーダルを構築する（Shadow Rootへの追加は呼び出し元が行う）。
 * 1タグ（1ウィジェットインスタンス）につき1つのモーダルインスタンスを共有利用する想定。
 * スタイル（`CALENDAR_MODAL_STYLES`）は呼び出し元が自身のCSS文字列に連結して
 * `applyStylesheet`で適用済みであることを前提とする（このファイル自体は`applyStylesheet`を呼ばない）。
 */
export function createCalendarModal(): CalendarModalHandle {
  const backdrop = createElement("div", { className: "spica-calendar-modal-backdrop", attrs: { hidden: "" } });
  const panel = createElement("div", {
    className: "spica-calendar-modal-panel",
    attrs: { role: "dialog", "aria-modal": "true", "aria-live": "polite" },
  });
  const closeButton = createElement("button", {
    className: "spica-calendar-modal-close",
    text: "×",
    attrs: { type: "button", "aria-label": "閉じる" },
  });
  const companyEl = createElement("p", { className: "spica-calendar-modal-company" });
  const contentEl = createElement("p", { className: "spica-calendar-modal-content" });
  const body = createElement("div", { className: "spica-calendar-modal-body" });
  panel.appendChild(closeButton);
  panel.appendChild(companyEl);
  panel.appendChild(contentEl);
  panel.appendChild(body);
  backdrop.appendChild(panel);

  let movedElement: HTMLElement | null = null;
  let originalParent: Node | null = null;
  let originalNextSibling: Node | null = null;
  let opener: HTMLElement | null = null;
  let onCloseCallback: (() => void) | null = null;

  const close = (): void => {
    if (backdrop.hasAttribute("hidden")) return;
    backdrop.setAttribute("hidden", "");
    if (movedElement && originalParent) {
      if (originalNextSibling) {
        originalParent.insertBefore(movedElement, originalNextSibling);
      } else {
        originalParent.appendChild(movedElement);
      }
    }
    movedElement = null;
    originalParent = null;
    originalNextSibling = null;
    if (opener && opener.isConnected) opener.focus();
    opener = null;
    const callback = onCloseCallback;
    onCloseCallback = null;
    callback?.();
  };

  const openWithElement = (bodyElement: HTMLElement, onClose?: () => void, options?: CalendarModalOpenOptions): void => {
    opener = document.activeElement as HTMLElement | null;
    originalParent = bodyElement.parentNode;
    originalNextSibling = bodyElement.nextSibling;
    movedElement = bodyElement;
    onCloseCallback = onClose ?? null;
    // 企業名・コンテンツ名の表示制御
    if (options?.company) {
      companyEl.textContent = options.company;
      companyEl.style.display = "";
    } else {
      companyEl.style.display = "none";
    }
    if (options?.content) {
      contentEl.textContent = options.content;
      contentEl.style.display = "";
    } else {
      contentEl.style.display = "none";
    }
    body.textContent = "";
    body.appendChild(bodyElement);
    // モック準拠: カレンダーモーダル内のDOM並び替え（reorderBody指定時のみ）
    if (options?.reorderBody) {
      reorderModalBody(bodyElement);
    }
    backdrop.removeAttribute("hidden");
  };

  closeButton.addEventListener("click", close);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) close();
  });
  document.addEventListener("keydown", (event) => {
    if (!backdrop.hasAttribute("hidden") && event.key === "Escape") {
      event.preventDefault();
      close();
    }
  });

  return { element: backdrop, openWithElement, close };
}

/**
 * カレンダーモーダル内のDOM並び替え（design-mockups準拠）。
 * desiredDateTimeInputUi.tsが生成する「候補欄→カレンダー→時間帯」の縦積み構造を、
 * モック準拠の「1.日時を選択（カレンダー＋時間帯）→ 2.候補日時を確認（候補欄横並び）」に再配置する。
 */
function reorderModalBody(bodyElement: HTMLElement): void {
  // desiredDateTimeInputUi.tsは .spica-desired-datetime-inputs ラッパー内に全て構築する
  const wrapper = bodyElement.querySelector(".spica-desired-datetime-inputs") as HTMLElement | null;
  const target = wrapper || bodyElement;

  // カレンダー系要素を検出
  const calendarHeader = target.querySelector(".spica-calendar-header") as HTMLElement | null;
  const calendarGrid = target.querySelector(".spica-calendar-grid") as HTMLElement | null;
  const daySlots = target.querySelector(".spica-calendar-day-slots") as HTMLElement | null;
  if (!calendarHeader || !calendarGrid) return;

  // 候補ラベル要素を収集（3つ揃っている場合のみ並び替えを行う）
  const candidateLabels = Array.from(target.querySelectorAll(".spica-request-candidate-label")) as HTMLElement[];
  if (candidateLabels.length < 3) return;

  // 候補欄をフレックスコンテナに集約（横並び）
  const candidatesContainer = createElement("div", { attrs: { "data-appointment-candidates": "" } });
  candidatesContainer.style.display = "flex";
  candidatesContainer.style.flexWrap = "nowrap";
  candidatesContainer.style.gap = "8px";
  candidatesContainer.style.overflowX = "auto";
  candidatesContainer.style.paddingBottom = "4px";

  for (const label of candidateLabels) {
    const value = label.nextElementSibling as HTMLElement | null;
    const item = createElement("div");
    item.style.flex = "1 1 0";
    item.style.minWidth = "0";
    item.appendChild(label);
    if (value && (value.classList.contains("spica-request-candidate-value") || value.classList.contains("spica-proposal-candidate-value-static"))) {
      value.style.marginBottom = "0";
      item.appendChild(value);
    }
    candidatesContainer.appendChild(item);
  }

  // 見出し要素を作成
  const dateTimeHeading = createElement("p", { className: "spica-input-label", text: "1. 日時を選択" });
  (dateTimeHeading as HTMLElement).style.fontSize = "13px";
  (dateTimeHeading as HTMLElement).style.marginBottom = "8px";
  const candidatesHeading = createElement("p", { className: "spica-input-label", text: "2. 候補日時を確認" });
  (candidatesHeading as HTMLElement).style.fontSize = "13px";
  (candidatesHeading as HTMLElement).style.marginBottom = "8px";

  // 「候補を確定する」ボタンを退避
  const confirmBtn = bodyElement.querySelector(".spica-confirm-button, .spica-request-confirm-btn") as HTMLElement | null;

  // 要素を並び替え（textContentで消すとイベントリスナーが壊れるので、insertBeforeで移動）
  // 先頭に見出し→カレンダー系を挿入、候補欄コンテナを後に、ボタンを最後に
  const firstChild = target.firstChild;
  target.insertBefore(dateTimeHeading, firstChild);
  target.insertBefore(calendarHeader, dateTimeHeading.nextSibling);
  target.insertBefore(calendarGrid, calendarHeader.nextSibling);
  if (daySlots) target.insertBefore(daySlots, calendarGrid.nextSibling);
  const afterCalendar = daySlots ? daySlots.nextSibling : calendarGrid.nextSibling;
  target.insertBefore(candidatesHeading, afterCalendar);
  target.insertBefore(candidatesContainer, candidatesHeading.nextSibling);
  if (confirmBtn) target.appendChild(confirmBtn);
}
